// Host keep-alive das abas internas do admin.
//
// COMO FUNCIONA (e por que é seguro no react-router v6.30 atual, SEM atualizar o roteador):
// - Todas as abas abertas ficam montadas ao mesmo tempo; a visibilidade é alternada por CSS
//   (`display:none`) — assim estado de React, DOM, rolagem e formulários são preservados sem desmontar.
// - Isolamento de rota por aba: cada painel sobrescreve APENAS o `UNSAFE_LocationContext` com a
//   localização FIXA da sua aba. Com isso `<Routes>`, `useLocation`, `useParams` e `useSearchParams`
//   dentro da aba resolvem a rota DAQUELA aba — uma aba inativa nunca passa a ler a rota da ativa.
//   O `UNSAFE_NavigationContext` NÃO é sobrescrito: `useNavigate`/`<Link>` continuam usando o history
//   real, então a aba ativa dirige a URL do navegador (e o realce da sidebar) normalmente.
// - As rotas admin são ABSOLUTAS e montadas sob uma rota de layout cujo `pathnameBase` é "/", então não
//   dependemos de resolução relativa de splat (evita a mudança do `v7_relativeSplatPath`).
//
// Este é o ÚNICO ponto do código que toca `UNSAFE_LocationContext`. Se um dia o react-router for
// atualizado, a validação precisa focar aqui.
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  NavigationType,
  UNSAFE_LocationContext as LocationContext,
  useLocation,
  useNavigate,
  type Location,
} from "react-router-dom";
import { toast } from "sonner";
import { PortalContainerProvider } from "@/lib/portalContainer";
import { TabRuntimeProvider } from "./TabRuntimeProvider";
import { useAdminTabs } from "@/store/useAdminTabs";
import { isTabbablePath, tabKeyOf, type TabLocation } from "./tabModel";

function toRouterLocation(loc: TabLocation): Location {
  return { pathname: loc.pathname, search: loc.search, hash: "", state: null, key: `tab:${loc.pathname}` };
}

function TabPanel({
  tabKey,
  active,
  location,
  children,
}: {
  tabKey: string;
  active: boolean;
  location: TabLocation;
  children: React.ReactNode;
}) {
  // Guardamos o nó DOM em estado (callback ref) para fornecê-lo como container de portais: Dialog/Select/
  // etc. passam a renderizar DENTRO do painel, então `display:none` + `inert` os escondem/neutralizam
  // preservando os dados preenchidos.
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const prevActiveRef = useRef(active);

  useLayoutEffect(() => {
    if (node) node.inert = !active;
    // Ao SAIR da aba, fecha overlays Radix abertos nela (Dialog/menu/popover/select): um modal aberto
    // aplica scroll-lock/aria-hidden no documento e capturaria foco sobre a aba que fica visível. Os dados
    // preenchidos vivem no estado da página (preservado pelo keep-alive) — reabrir restaura o conteúdo.
    if (prevActiveRef.current && !active) {
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    }
    prevActiveRef.current = active;
  }, [node, active]);

  const value = useMemo(
    () => ({ location: toRouterLocation(location), navigationType: NavigationType.Pop }),
    [location],
  );

  return (
    <div
      ref={setNode}
      aria-hidden={!active}
      className="flex-1 min-h-0 overflow-auto"
      style={{ display: active ? "block" : "none" }}
      data-tab-key={tabKey}
      data-tab-active={active}
    >
      <PortalContainerProvider value={node}>
        <TabRuntimeProvider tabKey={tabKey} active={active}>
          <LocationContext.Provider value={value}>{children}</LocationContext.Provider>
        </TabRuntimeProvider>
      </PortalContainerProvider>
    </div>
  );
}

export function TabKeepAliveHost({ children }: { children: React.ReactNode }) {
  const browserLoc = useLocation();
  const navigate = useNavigate();
  const tabs = useAdminTabs((s) => s.tabs);
  const activeKey = useAdminTabs((s) => s.activeKey);
  const sync = useAdminTabs((s) => s.sync);
  const goHome = useAdminTabs((s) => s.goHome);

  // Controlador síncrono (layout effect = sem flash/duplo-mount): cada mudança de URL reconcilia as abas.
  // É aqui que voltar/avançar e links diretos entram no modelo e onde o limite de 5 é aplicado.
  useLayoutEffect(() => {
    if (!isTabbablePath(browserLoc.pathname)) {
      // Caminho sem aba (ex.: "/admin" só redireciona): mostra o fallback-live sem fechar abas abertas.
      goHome();
      return;
    }
    const result = sync({ pathname: browserLoc.pathname, search: browserLoc.search });
    if (result.type === "blocked") {
      const target = result.revertTo;
      toast.error("Limite de 7 abas aberto. Feche uma aba antes de abrir outra.");
      if (tabKeyOf(target.pathname) !== tabKeyOf(browserLoc.pathname)) {
        navigate({ pathname: target.pathname, search: target.search }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserLoc.pathname, browserLoc.search]);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {tabs.map((tab) => (
        <TabPanel key={tab.key} tabKey={tab.key} active={tab.key === activeKey} location={tab.location}>
          {children}
        </TabPanel>
      ))}
      {/* Fallback renderizado só quando nenhuma aba está ativa: executa o redirect de caminhos como
          "/admin" e cobre o instante inicial antes da primeira aba abrir. Usa a localização real (sem
          override). Não monta páginas pesadas em paralelo porque só existe quando activeKey === null. */}
      {activeKey === null && <div className="flex-1 min-h-0 overflow-auto">{children}</div>}
    </div>
  );
}
